import React from 'react';
import { SectionProgressBar, SectionProgressBarProps } from '~frontend-common/components/ui';
import { useDeviceScreenSize } from '~frontend-common/hooks/use-device-screen-size';
import { AppBar, BaseNavbarContainer, MainToolBar, SecondToolBar, Spacer } from './base-navbar.resources';
import { NavbarLogo } from './navbar-items/navbar-logo';

export interface BaseNavbarProps {
  logoLink: {
    path: string;
    openNewTab?: boolean;
    external?: boolean;
  };

  /** Images object pointing to image paths */
  imageSrc: any;

  id?: string;

  /** children to render on the right side of the navbar */
  children?: React.ReactNode;

  /** progressBar options, the progress bar won't be displayed if options are undefined */
  progressBar?: SectionProgressBarProps;

  /** Custom content to add at the top of the header */
  additionalContent?: () => React.ReactNode;

  /** Defines whether the header should stick to the top of the screen. Sticky by default. */
  isSticky?: boolean | boolean[];

  /** Force the navbar to be rendered as if there's always a secondary toolbar. Used to make it look consistent on web */
  forceFullHeight?: boolean;
}

export function BaseNavbar({
  logoLink,
  imageSrc,
  id,
  children,
  progressBar,
  additionalContent,
  isSticky = [true, true, true],
  forceFullHeight = false,
}: BaseNavbarProps) {
  const { isMobile } = useDeviceScreenSize();
  const hasAdditionalContent = !!additionalContent;
  const hasSecondaryToolbar = !!progressBar;

  return (
    <BaseNavbarContainer
      id={id}
      $isSticky={isSticky}
      $hasSecondaryToolbar={hasSecondaryToolbar}
      $forceFullHeight={forceFullHeight}
    >
      <AppBar
        $dynamicHeight={hasAdditionalContent}
        $isSticky={isSticky}
        $hasSecondaryToolbar={hasSecondaryToolbar}
        $forceFullHeight={forceFullHeight}
      >
        {additionalContent?.()}

        <MainToolBar $hasSecondaryToolbar={hasSecondaryToolbar}>
          <NavbarLogo
            linkTo={logoLink.path}
            openNewTab={logoLink.openNewTab}
            external={logoLink.external}
            compact={isMobile}
            imageSrcCompact={imageSrc.header.logoCheck}
            imageSrcFull={imageSrc.header.logo}
          />

          <Spacer />

          {children}
        </MainToolBar>

        {progressBar && (
          <SecondToolBar>
            <SectionProgressBar {...progressBar} />
          </SecondToolBar>
        )}
      </AppBar>
    </BaseNavbarContainer>
  );
}
